export type FormStatus = "Active" | "Draft" | "Inactive";
export type FieldType = "text" | "phone" | "email" | "textarea" | "dropdown" | "radio" | "checkbox" | "date" | "number" | "property_type" | "budget_range" | "preferred_location" | "site_visit_date";

export interface FormField {
  id: string;
  type: FieldType;
  label: string;
  placeholder: string;
  required: boolean;
  width: "full" | "half";
  helpText: string;
  options: string[];
}

export interface LeadCaptureForm {
  id: string;
  name: string;
  description: string;
  status: FormStatus;
  submissions: number;
  lastSubmission: string;
  fieldCount: number;
  fields: FormField[];
  formTitle: string;
  formSubtitle: string;
  submitButtonText: string;
  privacyPolicyCheckbox: boolean;
  successMessage: string;
  mappedProjectId: string | null;
}

export const fieldTypeLabels: Record<FieldType, string> = {
  text: "Text Input",
  phone: "Phone Number",
  email: "Email Address",
  textarea: "Textarea (Long text)",
  dropdown: "Dropdown Select",
  radio: "Radio Buttons",
  checkbox: "Checkbox",
  date: "Date Picker",
  number: "Number Input",
  property_type: "Property Type",
  budget_range: "Budget Range",
  preferred_location: "Preferred Location",
  site_visit_date: "Site Visit Date",
};

export const fieldTypeIcons: Record<FieldType, string> = {
  text: "ri-text",
  phone: "ri-phone-line",
  email: "ri-mail-line",
  textarea: "ri-align-left",
  dropdown: "ri-arrow-down-s-line",
  radio: "ri-checkbox-blank-circle-line",
  checkbox: "ri-checkbox-line",
  date: "ri-calendar-line",
  number: "ri-hashtag",
  property_type: "ri-building-line",
  budget_range: "ri-money-rupee-circle-line",
  preferred_location: "ri-map-pin-line",
  site_visit_date: "ri-calendar-event-line",
};

export const basicFieldTypes: FieldType[] = ["text", "phone", "email", "textarea", "dropdown", "radio", "checkbox", "date", "number"];
export const specialFieldTypes: FieldType[] = ["property_type", "budget_range", "preferred_location", "site_visit_date"];
export const hiddenFieldTypes = [
  { type: "utm_source", label: "UTM Source (auto-capture)" },
  { type: "utm_campaign", label: "UTM Campaign (auto-capture)" },
  { type: "page_url", label: "Page URL (auto-capture)" },
];

export const defaultFormFields: FormField[] = [
  { id: "f1", type: "text", label: "Full Name", placeholder: "Enter your full name", required: true, width: "full", helpText: "", options: [] },
  { id: "f2", type: "phone", label: "Phone Number", placeholder: "Enter your phone number", required: true, width: "full", helpText: "Must be 10 digits", options: [] },
  { id: "f3", type: "email", label: "Email Address", placeholder: "Enter your email address", required: false, width: "full", helpText: "", options: [] },
  { id: "f4", type: "textarea", label: "Message", placeholder: "Write your message here...", required: false, width: "full", helpText: "", options: [] },
];

export const formsData: LeadCaptureForm[] = [
  {
    id: "form-1",
    name: "Main Contact Form",
    description: "Primary inquiry form for website homepage and property pages",
    status: "Active",
    submissions: 234,
    lastSubmission: "2 hrs ago",
    fieldCount: 6,
    formTitle: "Contact Us",
    formSubtitle: "Fill in your details and we'll get back to you shortly",
    submitButtonText: "Submit Inquiry",
    privacyPolicyCheckbox: true,
    successMessage: "Thank you! We'll contact you shortly.",
    mappedProjectId: "11111111-1111-4bbb-bbbb-111111111111",
    fields: [
      { id: "f1", type: "text", label: "Full Name", placeholder: "Enter your full name", required: true, width: "full", helpText: "", options: [] },
      { id: "f2", type: "phone", label: "Phone Number", placeholder: "Enter your phone number", required: true, width: "full", helpText: "Must be 10 digits", options: [] },
      { id: "f3", type: "email", label: "Email Address", placeholder: "Enter your email address", required: false, width: "full", helpText: "", options: [] },
      { id: "f4", type: "property_type", label: "Property Type", placeholder: "Select property type", required: true, width: "full", helpText: "", options: ["2BHK", "3BHK", "Villa", "Commercial"] },
      { id: "f5", type: "budget_range", label: "Budget Range", placeholder: "Select your budget", required: false, width: "full", helpText: "", options: ["Below 50L", "50L - 1Cr", "1Cr - 2Cr", "2Cr - 3Cr", "Above 3Cr"] },
      { id: "f6", type: "textarea", label: "Message", placeholder: "Write your message here...", required: false, width: "full", helpText: "", options: [] },
    ],
  },
  {
    id: "form-2",
    name: "Site Visit Request",
    description: "Dedicated form for scheduling property site visits",
    status: "Active",
    submissions: 89,
    lastSubmission: "1 day ago",
    fieldCount: 5,
    formTitle: "Request a Site Visit",
    formSubtitle: "Schedule your property visit at a convenient time",
    submitButtonText: "Request Visit",
    privacyPolicyCheckbox: true,
    successMessage: "Thank you! We'll confirm your visit slot shortly.",
    mappedProjectId: "11111111-1111-4bbb-bbbb-111111111111",
    fields: [
      { id: "f1", type: "text", label: "Full Name", placeholder: "Enter your full name", required: true, width: "full", helpText: "", options: [] },
      { id: "f2", type: "phone", label: "Phone Number", placeholder: "Enter your phone number", required: true, width: "full", helpText: "Must be 10 digits", options: [] },
      { id: "f3", type: "site_visit_date", label: "Preferred Date", placeholder: "Select preferred date", required: true, width: "full", helpText: "", options: [] },
      { id: "f4", type: "property_type", label: "Property Interest", placeholder: "Select property type", required: true, width: "full", helpText: "", options: ["2BHK", "3BHK", "Villa", "Commercial"] },
      { id: "f5", type: "textarea", label: "Message", placeholder: "Any specific requirements?", required: false, width: "full", helpText: "", options: [] },
    ],
  },
  {
    id: "form-3",
    name: "Quick Inquiry Widget",
    description: "Floating widget for quick inquiries on all pages",
    status: "Draft",
    submissions: 0,
    lastSubmission: "Never",
    fieldCount: 3,
    formTitle: "Quick Inquiry",
    formSubtitle: "Got a question? Ask us!",
    submitButtonText: "Send",
    privacyPolicyCheckbox: false,
    successMessage: "Thanks! We'll get back to you soon.",
    mappedProjectId: null,
    fields: [
      { id: "f1", type: "text", label: "Full Name", placeholder: "Your name", required: true, width: "full", helpText: "", options: [] },
      { id: "f2", type: "phone", label: "Phone Number", placeholder: "Your phone number", required: true, width: "full", helpText: "", options: [] },
      { id: "f3", type: "textarea", label: "Message", placeholder: "How can we help?", required: false, width: "full", helpText: "", options: [] },
    ],
  },
];

export const formSubmissionsData = [
  { id: "sub-1", submitted: "28 Jun 2026, 10:32 AM", name: "Vikram Mehta", phone: "9876543210", email: "vikram.m@email.com", sourcePage: "/properties/bandra-west", status: "Lead Created" as const },
  { id: "sub-2", submitted: "28 Jun 2026, 09:15 AM", name: "Anita Desai", phone: "9845123456", email: "anita.d@email.com", sourcePage: "/", status: "Lead Created" as const },
  { id: "sub-3", submitted: "27 Jun 2026, 05:45 PM", name: "Suresh Nair", phone: "9712345678", email: "suresh.n@email.com", sourcePage: "/properties/andheri", status: "Lead Created" as const },
  { id: "sub-4", submitted: "27 Jun 2026, 02:20 PM", name: "Kavita Joshi", phone: "9632145870", email: "", sourcePage: "/contact", status: "Duplicate" as const },
  { id: "sub-5", submitted: "27 Jun 2026, 11:10 AM", name: "Rahul Verma", phone: "9521478963", email: "rahul.v@email.com", sourcePage: "/properties/bandra-west", status: "Lead Created" as const },
  { id: "sub-6", submitted: "26 Jun 2026, 04:55 PM", name: "Deepika Singh", phone: "9415263780", email: "deepika.s@email.com", sourcePage: "/", status: "Lead Created" as const },
  { id: "sub-7", submitted: "26 Jun 2026, 01:30 PM", name: "Sunita Rao", phone: "9201364875", email: "sunita.r@email.com", sourcePage: "/properties/juhu", status: "Duplicate" as const },
  { id: "sub-8", submitted: "25 Jun 2026, 03:15 PM", name: "", phone: "9854123670", email: "", sourcePage: "/contact", status: "Error" as const },
  { id: "sub-9", submitted: "25 Jun 2026, 10:45 AM", name: "Arjun Kapoor", phone: "9308547621", email: "arjun.k@email.com", sourcePage: "/properties/thane", status: "Lead Created" as const },
  { id: "sub-10", submitted: "24 Jun 2026, 06:20 PM", name: "Pooja Menon", phone: "9741258036", email: "pooja.m@email.com", sourcePage: "/properties/bandra-west", status: "Lead Created" as const },
];

export interface LeadCaptureStats {
  activeForms: number;
  totalSubmissions: number;
  thisMonth: number;
  avgPerDay: number;
}

export const leadCaptureStats: LeadCaptureStats = {
  activeForms: 3,
  totalSubmissions: 1247,
  thisMonth: 234,
  avgPerDay: 7.8,
};

export function getFormStatusBadge(status: FormStatus): string {
  const map: Record<FormStatus, string> = {
    Active: "bg-green-50 text-green-700",
    Draft: "bg-amber-50 text-amber-700",
    Inactive: "bg-gray-100 text-gray-600",
  };
  return map[status] || "bg-gray-100 text-gray-600";
}

export function getSubmissionStatusBadge(status: string): string {
  const map: Record<string, string> = {
    "Lead Created": "bg-green-50 text-green-700",
    "Duplicate": "bg-amber-50 text-amber-700",
    "Error": "bg-red-50 text-red-700",
  };
  return map[status] || "bg-gray-100 text-gray-600";
}